import { ApiError, apiRequest } from "./apiClient";
import type {
  KbGenerationTemplateDetail,
  KbGenerationTemplateListResult,
  KbGenerationTemplateSaveResult,
} from "../types/gameRuleKbGenerationTemplate";

type SuccessResponse<T> = {
  readonly success: boolean;
  readonly data: T;
  readonly message?: string;
};

type TemplateListApi = {
  readonly items?: readonly {
    readonly id: string;
    readonly template_group_id: string;
    readonly name: string;
    readonly version: number;
    readonly section_count: number;
    readonly total_versions?: number;
    readonly updated_at: string;
  }[];
  readonly total?: number;
};

type TemplateDetailApi = {
  readonly id: string;
  readonly template_group_id: string;
  readonly name: string;
  readonly version: number;
  readonly section_count: number;
  readonly total_versions?: number;
  readonly section_ids?: readonly string[];
  readonly created_at: string;
  readonly updated_at: string;
};

type TemplateSaveApi = {
  readonly id: string;
  readonly template_group_id: string;
  readonly name: string;
  readonly version: number;
  readonly section_count: number;
  readonly is_version_update?: boolean;
};

async function parseDetail(response: Response): Promise<string> {
  const raw = await response.text();
  try {
    const parsed = JSON.parse(raw) as { detail?: string; message?: string };
    if (typeof parsed.detail === "string") {
      return parsed.detail;
    }
    if (typeof parsed.message === "string") {
      return parsed.message;
    }
    return raw || `HTTP ${response.status}`;
  } catch {
    return raw || `HTTP ${response.status}`;
  }
}

export type ListKbGenerationTemplatesOptions = {
  readonly limit?: number;
  readonly offset?: number;
};

export const listKbGenerationTemplates = async (
  search?: string,
  options?: ListKbGenerationTemplatesOptions,
): Promise<KbGenerationTemplateListResult> => {
  const query = new URLSearchParams();
  if (search?.trim()) {
    query.set("search", search.trim());
  }
  const limit = Math.max(1, Math.min(options?.limit ?? 20, 100));
  const offset = Math.max(0, options?.offset ?? 0);
  query.set("limit", String(limit));
  query.set("offset", String(offset));
  const response = await apiRequest(
    `/game-rule/kb/generate/templates?${query.toString()}`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<TemplateListApi>;
  const data = json.data ?? { items: [], total: 0 };
  const items = Array.isArray(data.items) ? data.items : [];
  return {
    items: items.map((row) => ({
      id: String(row.id),
      templateGroupId: String(row.template_group_id),
      name: row.name,
      version: row.version,
      sectionCount: row.section_count,
      totalVersions: row.total_versions ?? 1,
      updatedAt: row.updated_at,
    })),
    total: data.total ?? items.length,
  };
};

export const getKbGenerationTemplate = async (
  templateId: string,
): Promise<KbGenerationTemplateDetail> => {
  const response = await apiRequest(
    `/game-rule/kb/generate/templates/${encodeURIComponent(templateId)}`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<TemplateDetailApi>;
  const row = json.data;
  return {
    id: String(row.id),
    templateGroupId: String(row.template_group_id),
    name: row.name,
    version: row.version,
    sectionCount: row.section_count,
    totalVersions: row.total_versions ?? 1,
    sectionIds: Array.isArray(row.section_ids) ? row.section_ids.map(String) : [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

export const saveKbGenerationTemplate = async (
  name: string,
  sectionOrder: readonly string[],
  options?: {
    readonly sourceSessionId?: string;
    readonly sourceDocumentId?: string;
  },
): Promise<KbGenerationTemplateSaveResult> => {
  const response = await apiRequest("/game-rule/kb/generate/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: name.trim(),
      section_order: sectionOrder,
      source_session_id: options?.sourceSessionId ?? undefined,
      source_document_id: options?.sourceDocumentId ?? undefined,
    }),
  });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<TemplateSaveApi>;
  const data = json.data;
  return {
    id: String(data.id),
    templateGroupId: String(data.template_group_id),
    name: data.name,
    version: data.version,
    sectionCount: data.section_count,
    isVersionUpdate: Boolean(data.is_version_update),
  };
};
